/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import uniqueId from "lodash/uniqueId"
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type RelatedProductLink = {
    fields: {
        buttonText: string;
        copyText: string;
        carouselType: string;
    }
}

interface OwnProps extends WidgetProps {
    links: RelatedProductLink[]
};

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps> & HasHistory;

const mapStateToProps = (state: CustomAppState) => {
    const allowedProductRelations = (getSettingsCollection(state) as any)?.customSettings["relatedProductTypes"]?.split(
        ",",
    );
    const commonActions = state.xCommon;
    const availableRelatedProducts = allowedProductRelations.map((relation: string) =>
        commonActions[relation] ? relation : null,
    );
    return {
        availableRelatedProducts,
        commonActions
    };
};

export interface ViewRelatedItemsLinksStyles {
    whiteContainer?: InjectableCss;
    message?: TypographyProps;
    viewLink?: LinkPresentationProps;
}

export const viewRelatedItemsLinksStylesStyles: ViewRelatedItemsLinksStyles = {
    whiteContainer: {
        css: css`
            margin-top: 12px;
            background: white;
            padding: 8px;
            &&:nth-child(2n) {
                margin-top: 20px;
            }
            @media print {
                display: none;
            }
        `,
    },
    message: {
        size: "12px",
        lineHeight: "18px",
        css: css`
            margin-bottom: 8px;
            display: block;
        `,
    },
    viewLink: {
        typographyProps: {
            size: 12,
            weight: "bold",
        },
        icon: {
            iconProps: {
                src: "IconArrowRightCopper",
                size: 9,
            },
            position: "right",
        },
        color: "common.black",
        css: css`
            text-transform: capitalize;
        `,
    },
};

const styles = viewRelatedItemsLinksStylesStyles;

const ViewRelatedItemsLinks = ({ product, availableRelatedProducts, commonActions, fields: { links } }: Props) => {
    const scrollToCarouselView = (relation: string) => {
        const carouselEl = document.getElementById(`productCarousel_relatedProducts_${relation}`);
        if (carouselEl) {
            carouselEl.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
        }
    };

    if (!product || !availableRelatedProducts || !availableRelatedProducts.length) {
        return null;
    }

    return (
        <>
            {links && links.length > 0 &&
                links.map(({fields: { buttonText, copyText, carouselType }}: RelatedProductLink) => {
                    const hasRelatedProducts = commonActions[carouselType];
                    if(!hasRelatedProducts) {
                        return null;
                    }
                    return (
                        <StyledWrapper {...styles.whiteContainer} key={`viewRelatedItem_${uniqueId()}`}>
                            <Typography {...styles.message}>{copyText}</Typography>
                            <Link onClick={() => scrollToCarouselView(carouselType)} {...styles.viewLink}>
                                {buttonText}
                            </Link>
                        </StyledWrapper>
                    );
                }
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(withHistory(ViewRelatedItemsLinks))),
    definition: {
        displayName: "Related Items Links",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: "links",
                displayName: "Related Items Links",
                defaultValue: [],
                fieldType: "Translatable",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.buttonText;
                },
                fieldDefinitions: [
                    {
                        name: "buttonText",
                        displayName: "Link/Button Text",
                        editorTemplate: "TextField",
                        defaultValue: ""
                    },
                    {
                        name: "copyText",
                        displayName: "Copy Text",
                        editorTemplate: "TextField",
                        defaultValue: ""
                    },
                    {
                        name: "carouselType",
                        displayName: "Related Products Type",
                        editorTemplate: "SystemListDropDownField",
                        systemListName: "ProductRelationship",
                        defaultValue: ""
                    }
                ]
            }
        ]
    },
};

export default widgetModule;
